import { createContext, useContext } from "solid-js";

import { createPatientT } from "~/patient_i18n/i18n";
import { localeList } from "~/util/date/intl_dates";
import { formatDateIn } from "~/util/format";

import type { Deck } from "./types";

/** The words the slides draw around Twelve's and staff's own: built in per language on the server
 *  (patient_pages.treatment_presentation.deck), sent on a deck in another language. English is the default. */
export const ENGLISH_DECK_LABELS = {
  preparedFor: "Prepared for %{name}",
  whatItIs: "What it is",
  whyNeeded: "Why it's needed",
  whatToExpect: "What to expect",
  visitNumber: "Visit %{number}",
  visitsNumbers: "Visits %{numbers}",
  teethInYourPlan: "Teeth in your plan",
  teethInYourVisit: "Teeth in your visit",
  teethInVisit: "Teeth in visit %{number}",
  teethIn: "Teeth in %{name}",
  now: "Now",
  xRay: "X-ray",
  photo: "Photo",
  doctorsRecommendation: "Doctor's recommendation",
  goodToKnow: "Good to know",
  keepInMind: "Keep in mind",
  visits: { one: "%{count} visit", other: "%{count} visits" },
  inTheChair: "%{time} in the chair",
  inTheChairInAll: "%{visits} visits · %{time} in the chair in all",
  around: "Around %{date}",
  minutes: "%{count} min",
  hours: "%{count} hr",
  treatment: "Treatment",
  fee: "Fee",
  insuranceEstimate: "Insurance estimate",
  yourPart: "Your part",
  total: "Total",
  play: "Play %{name}",
  pageUnavailable: "This page is no longer available",
  videoUnavailable: "This video can't play here",
  pictureUnavailable: "Couldn't load this picture",
  decay: "Decay",
  earlyDecay: "Early decay",
  tartar: "Tartar",
  rootTipSpot: "Spot at the root tip",
  fillingEdge: "Filling or crown edge",
  yourRight: "Your right",
  yourLeft: "Your left",
  upper: "Upper",
  lower: "Lower",
};

export type DeckLabelKey = keyof typeof ENGLISH_DECK_LABELS;

export type DeckWords = {
  t: (key: DeckLabelKey, values?: Record<string, string | number>) => string;
  /** "Wednesday, 11/4/2026", in the deck's language. */
  dateWithWeekday: (date: string) => string;
  /** "45 min", "2 hr", "1 hr 30 min". */
  chairTime: (minutes: number) => string;
  /** "Visit 1", "Visits 1 and 3", "Visits 1, 2 and 4". */
  visitsPhrase: (visits: number[]) => string;
  /** A short date, as the deck's language writes it. */
  date: (date: string) => string;
};

export function deckWords(deck: Pick<Deck, "labels" | "locale">): DeckWords {
  const locale = deck.locale ?? "en";
  // The slides' own date format (en-US) in English.
  const dateLocale = locale === "en" ? "en-US" : locale;
  // A counted label is a hash of plural forms in Ukrainian and Russian; the patient pages' lookup picks the form.
  const lookup = createPatientT({
    locale,
    locales: [],
    suggestedLocale: null,
    strings: { ...ENGLISH_DECK_LABELS, ...deck.labels },
  });
  const t: DeckWords["t"] = (key, values) => lookup(key, values);
  return {
    t,
    dateWithWeekday: (date) =>
      formatDateIn(date, dateLocale, {
        weekday: "long",
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }),
    date: (date) => formatDateIn(date, dateLocale),
    chairTime: (minutes) => {
      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;
      if (hours === 0) return t("minutes", { count: rest });
      const hourText = t("hours", { count: hours });
      return rest === 0 ? hourText : `${hourText} ${t("minutes", { count: rest })}`;
    },
    visitsPhrase: (visits) => {
      const sorted = [...new Set(visits)].sort((a, b) => a - b);
      if (sorted.length === 0) return "";
      if (sorted.length === 1) return t("visitNumber", { number: sorted[0]! });
      // English keeps the slides' own "1, 2 and 4"; other languages join the list their own way.
      const list =
        locale === "en"
          ? `${sorted.slice(0, -1).join(", ")} and ${sorted.at(-1)}`
          : localeList(locale, sorted.map(String));
      return t("visitsNumbers", { numbers: list });
    },
  };
}

const DeckWordsContext = createContext<() => DeckWords>(() => deckWords({}));

/** Given the words of the deck a slide is drawn in, as a memo, so the slide follows its deck into another language. */
export const DeckWordsProvider = DeckWordsContext.Provider;

export function useDeckWords(): () => DeckWords {
  return useContext(DeckWordsContext);
}
